<template>
  <div>
    <div class="d-flex align-items-center mb-4">
      <div class="mr-auto">
        <h1 class="d-inline">Landing groups</h1>
        <button
          v-if="$can('create', 'Landing')"
          @click="showGroupForm()"
          class="btn btn-link-alt text-primary title-link ml-3"
          v-b-tooltip.hover
          title="Add new landing"
        >
          <svg-icon icon="plus-round" class="icon-xl" />
        </button>
      </div>
    </div>
    <ul class="list-unstyled" v-if="landings">
      <box tag="li" v-for="landing in landings" :key="landing.id">
        <div>
          <header>
            <h2>
              {{ landing.name }}
              <button
                v-if="$can('delete', 'Landing')"
                class="btn-link-alt float-right"
                title="Delete landings group"
                @click="deleteConfirm(landing)"
                v-b-tooltip.hover
              >
                <svg-icon icon="trash" class="icon-xl" />
              </button>
              <button
                v-if="$can('update', 'Landing')"
                class="btn-link-alt ml-3 edit-group"
                title="Edit group"
                @click="showGroupForm(landing)"
                v-b-tooltip.hover
              >
                <svg-icon icon="pencil" class="icon-lg" />
              </button>
            </h2>
          </header>
          <section>
            <div class="mb-3">
              <span class="text-light">Distribution algorithm: </span>
              <span class="font-weight-bold">
                {{ distributionName(landing.algorithm) }}
              </span>
            </div>
            <div v-if="landing.landings.length" class="table-responsive">
              <b-table
                class="mb-0"
                hover
                :fields="tableFields(landing)"
                :items="landing.landings"
              >
                <template
                  v-if="$can('change-status', 'Landing')"
                  slot="edit"
                  slot-scope="{ item }"
                >
                  <button
                    v-if="$can('change-status', 'Landing')"
                    class="btn btn-link-alt text-primary title-link"
                    @click="showChangeStatusForm(landing.id, item)"
                  >
                    Change status
                  </button>
                </template>
                <template slot="status" slot-scope="{ item }">
                  <b-badge
                    :variant="statusClass(item.status)"
                    class="text-uppercase ml-1"
                  >
                    {{ item.status }}
                    <svg-icon
                      v-if="item.status === 'rejected'"
                      icon="question-circle"
                      class="icon-lg reject-reason ml-1"
                      v-b-tooltip.hover
                      :title="item.rejectDescription"
                    />
                  </b-badge>
                </template>
                <template slot="url" slot-scope="{ item }">
                  <a :href="item.url" target="_blank">{{ item.url }}</a>
                </template>
              </b-table>
            </div>
          </section>
        </div>
      </box>
    </ul>

    <b-modal
      id="new-landing-group-form"
      ref="new-landing-group-form"
      hide-footer
      title="Add new landing page group"
      size="lg"
    >
      <landing-add
        :is-edit="edit.state"
        :group="edit.group"
        @form-submitted="hideGroupForm"
      />
    </b-modal>

    <b-modal
      v-if="$can('change-status', 'Landing')"
      id="change-landing-status-form"
      ref="change-landing-status-form"
      hide-footer
      title="Change status"
    >
      <landing-status
        :group-id="changeStatus.groupId"
        :landing="changeStatus.landing"
        @update-landings="updateList"
      />
    </b-modal>
  </div>
</template>

<script>
import LandingAdd from './components/LandingAdd.vue';
import LandingStatus from './components/LandingStatus.vue';
import { loadingMixin, statusClassesMixin } from '@/utils/mixins.js';
import { mapGetters } from 'vuex';

export default {
  components: {
    LandingAdd,
    LandingStatus
  },

  mixins: [loadingMixin, statusClassesMixin],

  head() {
    return {
      title() {
        return {
          inner: 'Landing groups'
        };
      }
    };
  },

  data() {
    return {
      editField: {
        edit: {
          label: 'Edit',
          key: 'edit',
          thClass: 'border-top-0 text-left pl-2',
          thStyle: {
            width: '150px'
          }
        }
      },
      commonFields: {
        status: {
          label: 'Status',
          key: 'status',
          sortable: true,
          thClass: 'border-top-0',
          thStyle: {
            width: '150px'
          }
        },
        url: {
          label: 'Url',
          key: 'url',
          sortable: true,
          thClass: 'pl-2 border-top-0'
        }
      },
      shareField: {
        rate: {
          label: 'Share %',
          key: 'rate',
          sortable: true,
          thClass: 'border-top-0 text-center',
          tdClass: 'text-center',
          thStyle: {
            width: '150px'
          }
        }
      },
      edit: {
        state: false,
        group: {}
      },
      changeStatus: {
        groupId: null,
        landingId: null
      }
    };
  },

  computed: {
    userId() {
      return this.$store.getters.user.id;
    },
    isAdmin() {
      return this.$store.getters.role === 'advert-admin';
    },
    baseFields() {
      return this.$can('change-status', 'Landing')
        ? { ...this.editField, ...this.commonFields }
        : this.commonFields;
    },
    ...mapGetters({
      landings: 'advert/landingGroups'
    })
  },

  methods: {
    tableFields(group) {
      return group.algorithm === 0
        ? this.baseFields
        : {
            ...this.baseFields,
            ...this.shareField
          };
    },
    showGroupForm(group) {
      if (!group) {
        this.edit = { state: false, group: {} };
      } else {
        this.edit = { state: true, group: group };
      }
      this.$refs['new-landing-group-form'].show();
    },
    hideGroupForm({ success }) {
      this.$refs['new-landing-group-form'].hide();
      this.edit = { state: false, group: {} };

      if (success) this.loadLandings();
    },
    distributionName(typeId) {
      return this.$store.getters['advert/getLandingDistributionById'](typeId)
        .name;
    },
    loadLandings() {
      if (this.isLoading) return;
      this.isLoading = true;
      const params = this.isAdmin ? {} : { ownerId: this.userId };

      this.$store.dispatch('advert/fetchLandingGroups', params).then(
        () => {
          this.isLoading = false;
        },
        e => {
          this.isLoading = false;
          if (this.landings.length === 0) {
            this.$store.commit('SET_PAGE_FAILURE', e);
          }
        }
      );
    },
    deleteConfirm(group) {
      this.$bvModal
        .msgBoxConfirm(
          `Please confirm that you want to delete landing group "${
            group.name
          }".`,
          {
            title: 'Please Confirm',
            size: 'sm',
            buttonSize: 'sm',
            okVariant: 'danger',
            okTitle: 'YES',
            cancelTitle: 'NO',
            footerClass: 'p-2',
            hideHeaderClose: false,
            centered: true
          }
        )
        .then(confirm => {
          if (confirm) this.deleteLandingGroup(group);
        });
    },
    deleteLandingGroup(group) {
      this.$api.adnet.delete(`/landing-groups/${group.id}`).then(
        () => {
          this.$notify('alert', {
            text: `Landing group - "${group.name}" - deleted.`,
            variant: 'danger',
            time: 5000
          });
          this.loadLandings();
        },
        e => {
          e.response.data.detail &&
            this.$notify('alert', {
              text: e.response.data.detail,
              variant: 'danger',
              time: 0
            });
        }
      );
    },
    showChangeStatusForm(groupId, landing) {
      this.changeStatus = { groupId: groupId, landing: landing };
      this.$refs['change-landing-status-form'].show();
    },
    updateList() {
      this.changeStatus = { groupId: null, landing: null };
      this.$refs['change-landing-status-form'].hide();
      this.loadLandings();
    }
  },

  created() {
    this.loadLandings();
  }
};
</script>

<style lang="scss" scoped>
.title-link,
.landing-edit {
  position: relative;
  top: -2px;
}
.reject-reason {
  position: relative;
  top: -1px;
}
.edit-group {
  position: relative;
  top: -2px;
}
</style>
